/**
 * Library Page - My Scenarios
 *
 * Displays saved scenarios with filtering, search, and management capabilities.
 * TASK-2026-076: Scenario Persistence (Database)
 */

import { useState, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import {
  useScenarios,
  useDeleteScenario,
  useDuplicateScenario,
  useRunScenario,
} from '@/hooks/useScenarios';
import type { Scenario, ScenarioType, ScenarioStatus } from '@/api/client';

// Icons
const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// Type badge colors
const typeBadgeStyles: Record<ScenarioType, string> = {
  launch: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  pricing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  portfolio: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  market: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
};

// Status badge colors
const statusBadgeStyles: Record<ScenarioStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  running: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// Scenario Card Component
function ScenarioCard({
  scenario,
  onRun,
  onDuplicate,
  onDelete,
  isRunning,
}: {
  scenario: Scenario;
  onRun: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isRunning: boolean;
}) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatMetric = (value: number | string | undefined) => {
    if (value === undefined) return '—';
    if (typeof value === 'number') {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
      return value.toFixed(1);
    }
    return value;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            {scenario.name}
          </h3>
          {scenario.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
              {scenario.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeBadgeStyles[scenario.scenarioType]}`}>
            {scenario.scenarioType}
          </span>
        </div>
      </div>

      {/* Meta info */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-3">
        <span>v{scenario.version}</span>
        <span>{formatDate(scenario.updatedAt)}</span>
        <span className={`px-2 py-0.5 rounded-full ${statusBadgeStyles[scenario.status]}`}>
          {scenario.status}
        </span>
        {scenario.isBaseline && (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
            baseline
          </span>
        )}
      </div>

      {/* Results preview */}
      {scenario.latestResult?.metrics && (
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-md p-3 mb-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            {Object.entries(scenario.latestResult.metrics).slice(0, 3).map(([key, value]) => (
              <div key={key}>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatMetric(value)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {key.replace(/_/g, ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-slate-700">
        <button
          onClick={onRun}
          disabled={isRunning || scenario.status === 'running'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-md transition-colors"
        >
          <PlayIcon />
          {isRunning ? 'Running...' : 'Run'}
        </button>
        <button
          onClick={onDuplicate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md transition-colors"
        >
          <CopyIcon />
          Duplicate
        </button>
        {showConfirmDelete ? (
          <div className="flex items-center gap-1 ml-auto">
            <button
              onClick={() => {
                onDelete();
                setShowConfirmDelete(false);
              }}
              className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
            >
              Confirm
            </button>
            <button
              onClick={() => setShowConfirmDelete(false)}
              className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowConfirmDelete(true)}
            className="ml-auto p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
            title="Delete scenario"
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
        <FolderIcon />
      </div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {hasFilters ? 'No matching scenarios' : 'No scenarios yet'}
      </h3>
      <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
        {hasFilters
          ? 'Try adjusting your filters or search terms.'
          : 'Create your first scenario from the Decide page to run simulations and save results.'}
      </p>
    </div>
  );
}

export function LibraryPage() {
  const { t } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ScenarioType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ScenarioStatus | 'all'>('all');
  const [runningScenarios, setRunningScenarios] = useState<Set<string>>(new Set());

  // Queries & mutations
  const { data: scenarios = [], isLoading, error } = useScenarios(clientId);
  const deleteScenario = useDeleteScenario(clientId || '');
  const duplicateScenario = useDuplicateScenario(clientId || '');
  const runScenario = useRunScenario(clientId || '');

  // Filtered scenarios
  const filteredScenarios = useMemo(() => {
    return scenarios.filter((s) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = s.name.toLowerCase().includes(query);
        const matchesDescription = s.description?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }
      // Type filter
      if (typeFilter !== 'all' && s.scenarioType !== typeFilter) return false;
      // Status filter
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      return true;
    });
  }, [scenarios, searchQuery, typeFilter, statusFilter]);

  // Group scenarios by type
  const groupedScenarios = useMemo(() => {
    const groups: Record<ScenarioType, Scenario[]> = {
      launch: [],
      pricing: [],
      portfolio: [],
      market: [],
    };
    filteredScenarios.forEach((s) => {
      groups[s.scenarioType].push(s);
    });
    return groups;
  }, [filteredScenarios]);

  // Stats
  const stats = useMemo(() => ({
    total: scenarios.length,
    completed: scenarios.filter((s) => s.status === 'completed').length,
    draft: scenarios.filter((s) => s.status === 'draft').length,
  }), [scenarios]);

  // Handlers
  const handleRun = async (scenarioId: string) => {
    setRunningScenarios((prev) => new Set([...prev, scenarioId]));
    try {
      await runScenario.mutateAsync(scenarioId);
    } finally {
      setRunningScenarios((prev) => {
        const next = new Set(prev);
        next.delete(scenarioId);
        return next;
      });
    }
  };

  const handleDuplicate = async (scenarioId: string) => {
    await duplicateScenario.mutateAsync({ scenarioId });
  };

  const handleDelete = async (scenarioId: string) => {
    await deleteScenario.mutateAsync(scenarioId);
  };

  const hasFilters = !!(searchQuery || typeFilter !== 'all' || statusFilter !== 'all');

  // No client selected
  if (!clientId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Select a Client
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Choose a client from the sidebar to view saved scenarios.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              My Scenarios
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {stats.total} scenarios ({stats.completed} completed, {stats.draft} drafts)
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search scenarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <FilterIcon />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as ScenarioType | 'all')}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="launch">Launch</option>
              <option value="pricing">Pricing</option>
              <option value="portfolio">Portfolio</option>
              <option value="market">Market</option>
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ScenarioStatus | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="draft">Draft</option>
            <option value="running">Running</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">
              Failed to load scenarios. Please try again.
            </p>
          </div>
        ) : filteredScenarios.length === 0 ? (
          <EmptyState hasFilters={hasFilters} />
        ) : (
          <div className="space-y-8">
            {/* Launch Scenarios */}
            {groupedScenarios.launch.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-xl">🚀</span> Launch Scenarios
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedScenarios.launch.map((scenario) => (
                    <ScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      onRun={() => handleRun(scenario.id)}
                      onDuplicate={() => handleDuplicate(scenario.id)}
                      onDelete={() => handleDelete(scenario.id)}
                      isRunning={runningScenarios.has(scenario.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Pricing Scenarios */}
            {groupedScenarios.pricing.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-xl">💰</span> Pricing Scenarios
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedScenarios.pricing.map((scenario) => (
                    <ScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      onRun={() => handleRun(scenario.id)}
                      onDuplicate={() => handleDuplicate(scenario.id)}
                      onDelete={() => handleDelete(scenario.id)}
                      isRunning={runningScenarios.has(scenario.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Portfolio Scenarios */}
            {groupedScenarios.portfolio.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-xl">📊</span> Portfolio Scenarios
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedScenarios.portfolio.map((scenario) => (
                    <ScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      onRun={() => handleRun(scenario.id)}
                      onDuplicate={() => handleDuplicate(scenario.id)}
                      onDelete={() => handleDelete(scenario.id)}
                      isRunning={runningScenarios.has(scenario.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Market Scenarios */}
            {groupedScenarios.market.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-xl">🌍</span> Market Scenarios
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedScenarios.market.map((scenario) => (
                    <ScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      onRun={() => handleRun(scenario.id)}
                      onDuplicate={() => handleDuplicate(scenario.id)}
                      onDelete={() => handleDelete(scenario.id)}
                      isRunning={runningScenarios.has(scenario.id)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
